import { USE_PARENT_OBJECT } from '@modules/common/constants';
import {
  DENOMINATOR_QUERY_ALIAS,
  JOIN_QUERY_ALIAS,
} from '@modules/quantify/constants';
import { useGetQuantifyBaseEQLProps } from '@modules/quantify/hooks/useGetQuantifyBaseEQLProps';
import { useGetQuantifyDenominatorJoinClause } from '@modules/quantify/hooks/useGetQuantifyDenominatorJoinClause';
import { useShouldAddDenominatorClause } from '@modules/quantify/hooks/useShouldAddDenominatorClause';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { VisualisationType } from '@modules/quantify/store/types';
import { EQLFunctionName, Join } from 'v2/core/repository/eql/types';
import { shallow } from 'zustand/shallow';

interface useGetQuantifyTableJoinClauseParams {
  shouldFetchCount?: boolean;
}

export function useGetQuantifyTableJoinClause({
  shouldFetchCount = false,
}: useGetQuantifyTableJoinClauseParams): Join | undefined {
  const { aggregatePeriod, ...eql } = useGetQuantifyBaseEQLProps();
  const {
    activeVisualisationType,
    shouldShowCooccurringDataPoints,
    formulaID,
    measuredAsCriteria,
  } = useQuantifyStore(({ metadata, query }) => {
    return {
      activeVisualisationType: metadata.activeVisualisationType,
      shouldShowCooccurringDataPoints: metadata.shouldShowCooccurringDataPoints,
      formulaID: query.formulaID,
      measuredAsCriteria: query.measuredAsCriteria,
    };
  }, shallow);
  const shouldAddDenominatorClause = useShouldAddDenominatorClause();

  const denominatorJoinClause = useGetQuantifyDenominatorJoinClause({
    alias: DENOMINATOR_QUERY_ALIAS,
  });

  const getFormulaFunctionName = () => {
    if (shouldFetchCount) {
      return EQLFunctionName.Count;
    }

    return formulaID;
  };

  // Total or cumulative values are required when analyzing time-series or sentiment data.
  if (activeVisualisationType === VisualisationType.TREND) {
    const formulaParams = measuredAsCriteria?.field
      ? [
          measuredAsCriteria?.field,
          ...(shouldFetchCount ? [USE_PARENT_OBJECT] : []),
          ...(shouldAddDenominatorClause ? [DENOMINATOR_QUERY_ALIAS] : []),
        ]
      : undefined;

    const functions = [];

    if (!shouldShowCooccurringDataPoints) {
      functions.push({
        name: EQLFunctionName.ApplyTaxonomy,
      });
    }

    functions.push({
      name: getFormulaFunctionName(),
      params: formulaParams,
    });

    return {
      eql: {
        ...eql,
        functions,
        joins: denominatorJoinClause ? [denominatorJoinClause] : undefined,
      },
      alias: JOIN_QUERY_ALIAS,
    };
  }

  return undefined;
}
